import { http } from 'utils'

const state = {
  rows: [],
  months: [],
  totals: {},
  targets: {},
  kpis: {},
  categories: [],
  sectionTotals: {},
  remaining: {},
  chartBreakdown: {},
  grandTotal: 0,
  yearTarget: 0,
  yearRemaining: 0,
  financialYear: null,
  loading: false
}

const getters = {
  // Buckets rows into their (backend-assigned) section for display, preserving section order.
  groupedRows: (state) => {
    const groups = {
      'Opportunities': [],
      'SLA': [],
      'Retainer': [],
      'T&M': [],
      'DevOps': [],
      'Hosting': [],
      'Unknown': []
    }
    state.rows.forEach(row => {
      if (groups[row.section]) {
        groups[row.section].push(row)
      }
    })
    return groups
  },

  monthlyRemaining: (state) => state.remaining
}

const actions = {
  // Pass silent: true to refresh the data in place without tearing down the grid.
  async fetchForecast({ commit }, { year, category, clientId, silent }) {
    if (!silent) {
      commit('SET_LOADING', true)
    }
    try {
      const params = { financial_year: year }
      if (category) {
        params.category = category
      }
      if (clientId) {
        params.client_id = clientId
      }
      const { data } = await http.get(`/forecast`, { params })
      commit('SET_FORECAST_DATA', data)
    } finally {
      if (!silent) {
        commit('SET_LOADING', false)
      }
    }
  },

  async addTentativeRow({ commit, state }, { name, group }) {
    const { data } = await http.post('/forecast/entries', { name, group })
    const entry = data.data
    commit('ADD_ROW', {
      type: 'tentative',
      forecast_entry_id: entry.id,
      name: entry.name,
      group: entry.group,
      section: entry.group === 'Opportunity' ? 'Opportunities' : entry.group,
      row_status: 'Tentative',
      fy_total: 0,
      months: {}
    })
    return entry
  },

  async updateTentativeRowName({ commit }, { entryId, name }) {
    await http.put(`/forecast/entries/${entryId}`, { name })
    commit('UPDATE_ROW_NAME', { entryId, name })
  },

  async updateTentativeMonth(context, { entryId, month, amount }) {
    await http.post(`/forecast/entries/${entryId}/months`, { month: `${month}-01`, amount })
  },

  async removeTentativeRow({ commit, state }, entryId) {
    await http.delete(`/forecast/entries/${entryId}`)
    commit('REMOVE_ROW', entryId)
  },

  async removeTentativeMonth(context, { entryId, monthId }) {
    await http.delete(`/forecast/entries/${entryId}/months/${monthId}`)
  }
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_FORECAST_DATA(state, data) {
    state.rows = data.rows
    state.months = data.months
    state.totals = data.totals
    state.targets = data.targets
    state.kpis = data.kpis || {}
    state.categories = data.categories || []
    state.sectionTotals = data.section_totals || {}
    state.remaining = data.remaining || {}
    state.chartBreakdown = data.chart_breakdown || {}
    state.grandTotal = data.grand_total || 0
    state.yearTarget = data.year_target || 0
    state.yearRemaining = data.year_remaining || 0
    state.financialYear = data.financial_year
  },

  ADD_ROW(state, row) {
    state.rows.push(row)
  },

  REMOVE_ROW(state, entryId) {
    state.rows = state.rows.filter(r => !(r.type === 'tentative' && r.forecast_entry_id === entryId))
  },

  UPDATE_ROW_NAME(state, { entryId, name }) {
    const row = state.rows.find(r => r.type === 'tentative' && r.forecast_entry_id === entryId)
    if (row) row.name = name
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
